import { Suspense, useMemo } from "react"
import classNames from "classnames"

import equal from "fast-deep-equal"

import { Loading, useCurrentView } from "web/components"

import { isNotAValue } from "web/services/helpers"

import SelectionSummary from "./SelectionSummary"
import Legend from "./Legend"
import useMapSettings from "./Settings"
import { SettingsMap } from "web/components/Map/index"

const lngLatFn = (x) => [
  Number.parseFloat(x.longitude),
  Number.parseFloat(x.latitude),
]

function getDistinctCategories(subjects, categoryField) {
  if (categoryField === undefined) return []

  const key = categoryField.key

  return subjects.reduce((distinct, subject) => {
    if (isNotAValue(subject.values[key])) return distinct

    const value = subject.values[key].value
    if (value.value === null) return distinct

    return distinct.find((elInDistinct) => equal(elInDistinct, value))
      ? distinct
      : [...distinct, value]
  }, [])
}

export default function LocationMap({
  subjectType,
  topic,
  primarySubject,
  selected,
  subjects,
  locations,
  locationInFocus,
  centralLocation,
  showSelectionSummary = true,
  onLocationClick,
  onItemClick,
  onSelectionChanged,
  detailCard,
  isLoading = false,
  showOrdinals = false,
}) {
  const DetailCard = detailCard

  const { view } = useCurrentView()

  const distanceCondition = view.conditions.find(
    (condition) => condition.type === "SpatialTypeDistanceCondition"
  )

  const hasLocationInFocus = locationInFocus !== undefined
  const hasTopic = !!topic

  const { categoryField, handleChangeSettings } = useMapSettings(subjectType)

  const categories = useMemo(
    () => getDistinctCategories(subjects, categoryField),
    [subjects, categoryField]
  )

  const cardWrapperClasses = classNames("absolute top-4 right-4 z-10", {
    "bottom-4": hasTopic,
  })

  return (
    <SettingsMap
      locations={locations}
      centralLocation={centralLocation}
      lngLatFn={lngLatFn}
      categoryField={categoryField}
      categories={categories}
      onLocationClick={onLocationClick}
      buttons={[{ onClick: handleChangeSettings, iconName: "cog" }]}
      distanceCondition={distanceCondition}
      isLoading={isLoading}
      showOrdinals={showOrdinals}
    >
      <div className={cardWrapperClasses}>
        {hasLocationInFocus && !!primarySubject && (
          <Suspense fallback={<Loading />}>
            <DetailCard
              subjectType={subjectType}
              topic={topic}
              primarySubject={primarySubject}
              onItemClick={onItemClick}
              onSelectionChanged={onSelectionChanged}
              onClose={() => onLocationClick()}
              {...locationInFocus}
            />
          </Suspense>
        )}
        {showSelectionSummary && selected.length !== 0 && (
          <SelectionSummary
            selected={selected}
            onSelectionChanged={onSelectionChanged}
          />
        )}
      </div>
      {!!categoryField && (
        <div className="absolute bottom-4 left-4 z-10">
          <Legend
            title={categoryField.label}
            categories={categories}
            isLoading={isLoading}
          />
        </div>
      )}
    </SettingsMap>
  )
}
